import { useState, type FormEvent } from 'react'
import { answerSocQuestion, type SocQuestion } from '../../lib/api'
import { btn, btnGhost, card, kicker, routeError, routeStatus, when } from './SocShell'
import { cn } from '../../lib/utils'

// One open question from /soc/needs-you, answerable in place: Yes / No / a
// sentence, posted to /soc/needs-you/{ref}/answer. What the SOC does next is
// the route's own sentence (`next`); the buttons go once it has answered.
//
// `canAnswer` is false where the route would refuse: a plain member cannot
// answer the analyst's question about themselves (403, E2a 605ebae). No box
// that will fail is ever drawn; the card says who answers instead.

export function SocQuestionCard({
  item,
  canAnswer,
  variant = 'owner',
}: {
  item: SocQuestion
  canAnswer: boolean
  variant?: 'owner' | 'member'
}) {
  const [next, setNext] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [closed, setClosed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [text, setText] = useState('')

  // A member is shown only the question asked of them, never the analyst's
  // assessment about their own account (review of E2b, 27 Oct 2026).
  // E5c: an escalation the owner can answer. They read what the analyst
  // found, then the question; it is never shown to a member.
  const escalation = item.kind === 'escalation' && variant !== 'member'
  const words = variant === 'member' ? item.question : item.question || item.assessment
  const asked = when(item.asked_at)
  const label =
    variant === 'member'
      ? 'Your SOC asks'
      : escalation
        ? ['Your SOC is working on this', item.person, asked && `since ${asked}`]
            .filter(Boolean)
            .join(' · ')
        : [item.person ? `Question for ${item.person}` : 'Question', asked && `asked ${asked}`]
            .filter(Boolean)
            .join(' · ')

  async function send(answer: string, notUs = false) {
    const value = answer.trim()
    if (!value || busy) return
    setBusy(true)
    setError(null)
    try {
      const out = notUs
        ? await answerSocQuestion(item.ref, value, true)
        : await answerSocQuestion(item.ref, value)
      setNext(out.next)
    } catch (err) {
      setError(routeError(err, 'We could not send your answer. Please try again.'))
      // The route said no for good (not yours, not open, gone): no retry box.
      const s = routeStatus(err)
      if (s === 403 || s === 404 || s === 409) setClosed(true)
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    void send(text)
  }

  const open = canAnswer && next === null && !closed

  // The owner's card is a row: the question takes the width, the answer box
  // sits beside it and wraps under it on a narrow screen; the flex-basis
  // values are widths. The member's card stacks the two, and in a column a
  // flex-basis is a height: 420px of question and 300px of Yes / No, mostly
  // empty (E5a, 3 Nov 2026). So the column gets no basis at all and the card
  // is as tall as its words.
  const column = variant === 'member'

  return (
    <div
      className={cn(
        card,
        'flex gap-4 p-5 sm:px-6',
        column
          ? 'flex-col items-stretch border-t-[3px] border-t-brass'
          : 'flex-wrap items-center',
      )}
      data-testid={escalation ? 'soc-escalation' : 'soc-question'}
    >
      <div className={cn('min-w-0', !column && 'flex-[999_1_420px]')}>
        <p className={cn(kicker, 'm-0 mb-1.5', escalation && 'text-danger')}>{label}</p>
        {escalation && item.assessment && (
          <p className="m-0 mb-2 text-[15px] leading-snug whitespace-pre-line text-[#3d3427]">
            {item.assessment}
          </p>
        )}
        {escalation ? (
          <p className="m-0 mb-1.5 text-lg leading-snug">{item.question}</p>
        ) : words ? (
          <p className="m-0 mb-1.5 text-lg leading-snug whitespace-pre-line">{words}</p>
        ) : (
          <p className="m-0 mb-1.5 text-[15px] text-[#6b5a3e]">
            The question was not put in words.
          </p>
        )}
        {!canAnswer && !escalation && (
          <p className="m-0 text-sm text-[#6b5a3e]">
            Your SOC asked the owner of the business this one; the owner answers it.
          </p>
        )}
      </div>
      {open && (
        <div className={cn('flex flex-col gap-2.5', !column && 'flex-[1_1_300px]')}>
          {escalation ? (
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                className={cn(btnGhost, 'flex-1')}
                disabled={busy}
                onClick={() => void send('That was us or expected')}
              >
                That was us or expected
              </button>
              <button
                type="button"
                className={cn(btn, 'flex-1')}
                disabled={busy}
                onClick={() => void send('That was not us', true)}
              >
                That was not us
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              <button type="button" className={cn(btn, 'flex-1')} disabled={busy} onClick={() => void send('Yes')}>
                Yes
              </button>
              <button type="button" className={cn(btnGhost, 'flex-1')} disabled={busy} onClick={() => void send('No')}>
                No
              </button>
            </div>
          )}
          <form onSubmit={onSubmit} className="flex flex-wrap gap-2.5">
            <label htmlFor={`answer-${item.ref}`} className="sr-only">
              Answer in your own words
            </label>
            <input
              id={`answer-${item.ref}`}
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Or answer in your own words"
              maxLength={4000}
              className="min-h-[44px] min-w-0 flex-[1_1_200px] rounded-md border border-[#cfc5b3] bg-white px-3.5 text-base"
            />
            <button type="submit" className={btnGhost} disabled={busy || !text.trim()}>
              Send
            </button>
          </form>
        </div>
      )}
      {next !== null && (
        <p className="m-0 text-[15px] font-bold text-[#8a6830]" role="status">
          {next}
        </p>
      )}
      {error && (
        <p className="m-0 text-[15px] text-danger" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}
